import * as THREE from 'three';
import Application from '../Application';
// Outils de relief partages avec DeskSet : le meme procede peint le clavier,
// le bureau, la tasse et les documents.
import { mergedMesh, paintVerticalGradient, srgb, tintedBox, } from './Procedural';
import { BRUNO_DESK } from './DecorMode';
import { SCREEN_SIZE, SCREEN_POSITION, SCREEN_TILT_X, BEZEL_T, } from './ScreenConfig';
/**
 * In-place gamer restyle of the baked room. The baked meshes can't be reshaped,
 * so the approach is: hide the named node we want gone, and add procedural
 * geometry in its place.
 *
 *  - vase (plant)        : removed (it clipped into the new PC tower)
 *  - old PC tower ("computer") : removed (replaced by WatercoolingTower)
 *  - monitor             : old shell hidden, replaced by a slim modern 16:9
 *                          bezel + stand framing the existing display
 *  - keyboard/mouse      : old models hidden, replaced by procedural
 *                          peripherals at the same spot (clavier ANSI avec
 *                          vraie grille de touches, souris galbee)
 *  - cables              : masques — ils passaient derriere le moniteur et ne
 *                          reliaient plus rien, l'ancien boitier et l'ancienne
 *                          coque d'ecran etant deja caches
 *
 * Monitor placement/size comes from ScreenConfig (shared with MonitorScreen
 * and CameraKeyframes so they never drift apart). Fine-tune live via URL
 * params without rebuilding, e.g. ?monY=20&standH=1&kbGlow=1.3
 */
/**
 * Disposition ANSI, largeurs en unites de touche (1u = une touche carree).
 * Les valeurs negatives sont des vides. C'est la variete des largeurs — Tab,
 * Verr. Maj, Entree, Maj, barre d'espace — qui fait lire l'objet comme un
 * clavier ; une grille de touches identiques ne trompe personne. Chaque
 * rangee totalise 15u.
 */
const KB_ROWS = [
    // Echap, vide, F1-F4, vide, F5-F8, vide, F9-F12
    [1, -1, 1, 1, 1, 1, -0.5, 1, 1, 1, 1, -0.5, 1, 1, 1, 1],
    // ² & e " ' ( ... ) = et Retour arriere
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2],
    // Tab, 12 touches, Entree haut
    [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.5],
    // Verr. Maj, 11 touches, Entree
    [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.25],
    // Maj, 10 touches, Maj
    [2.25, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.75],
    // Ctrl, Win, Alt, Espace, Alt Gr, Win, Menu, Ctrl
    [1.25, 1.25, 1.25, 6.25, 1.25, 1.25, 1.25, 1.25],
];
// Bloc de droite d'un clavier TKL, aligne sur les memes rangees. 0 = vide.
const KB_NAV = [
    [1, 1, 1],
    [1, 1, 1],
    [1, 1, 1],
    [0, 0, 0],
    [0, 1, 0],
    [1, 1, 1], // Gauche, Bas, Droite
];
const SCREEN = {
    height: SCREEN_SIZE.h,
    position: SCREEN_POSITION,
    tiltX: SCREEN_TILT_X,
};
export default class GamerSetup {
    constructor() {
        this.rgbMaterials = [];
        this.application = new Application();
        this.scene = this.application.scene;
        this.time = this.application.time;
        this.removeVase();
        this.removeOldTower();
        this.removeCables();
        this.replaceMonitor();
        this.replacePeripherals();
    }
    params() {
        const p = new URLSearchParams(window.location.search);
        const num = (k, d) => {
            const v = p.get(k);
            return v === null ? d : parseFloat(v);
        };
        return {
            monX: num('monX', 0),
            monY: num('monY', 0),
            monZ: num('monZ', 0),
            standH: num('standH', 1),
            kbGlow: num('kbGlow', 1),
        };
    }
    /** Hide the plant/vase that clips into the PC tower. */
    removeVase() {
        const plant = this.scene.getObjectByName('plant');
        if (plant)
            plant.visible = false;
    }
    /**
     * Masque la gerbe de cables du modele d'origine. Elle passait derriere le
     * moniteur, et comme l'ancien boitier et l'ancienne coque d'ecran sont
     * deja caches, ces cables ne reliaient plus rien.
     */
    removeCables() {
        const cables = this.scene.getObjectByName('cables');
        if (cables)
            cables.visible = false;
    }
    /** Hide the old white PC case — replaced by WatercoolingTower next to the desk. */
    removeOldTower() {
        const oldTower = this.scene.getObjectByName('computer');
        if (oldTower)
            oldTower.visible = false;
    }
    /**
     * Hide the old CRT shell and build a slim modern flat-panel around the
     * display, PLUS a separate, upright stand that reaches down to the real
     * desk surface.
     *
     * Bug this fixes: the stand used to be a child of the tilted screen
     * group (inheriting its ~3° tilt) with a fixed guessed height, so it
     * never actually touched the desk — it "floated". Now the stand is a
     * sibling that stays perfectly vertical and its height is computed live
     * from the desk's own bounding box, so it always reaches the desk
     * regardless of screen tilt or position tweaks.
     */
    replaceMonitor() {
        const { monX, monY, monZ, standH } = this.params();
        const monitor = this.scene.getObjectByName('monitor_base');
        if (monitor)
            monitor.visible = false;
        const h = SCREEN.height;
        const screenPos = SCREEN.position
            .clone()
            .add(new THREE.Vector3(monX, monY, monZ));
        // --- Bezel + panel (tilted to match the screen) ---
        const bezelGroup = new THREE.Group();
        bezelGroup.position.copy(screenPos);
        bezelGroup.rotation.x = SCREEN.tiltX;
        this.bezelGroup = bezelGroup;
        this.fillBezel();
        this.scene.add(bezelGroup);
        // --- Stand: separate, upright, reaches down to the desk surface ---
        const desk = this.scene.getObjectByName('desk');
        let deskTopY = screenPos.y - h / 2 - 360; // fallback if desk isn't found
        if (desk) {
            desk.updateWorldMatrix(true, true);
            deskTopY = new THREE.Box3().setFromObject(desk).max.y;
        }
        // Approximate world Y of the screen's bottom edge (tilt is only ~3°,
        // negligible for this — the stand only needs to look attached).
        const screenBottomY = screenPos.y - h / 2;
        const neckH = Math.max(40, (screenBottomY - deskTopY) * standH);
        const standGroup = new THREE.Group();
        // Sit slightly behind the screen, same X, resting exactly on the desk.
        standGroup.position.set(screenPos.x, deskTopY, screenPos.z - 26);
        // Lame plate et socle ovale, plutot que deux paves : c'est la
        // silhouette d'un moniteur de bureau recent, et le pave de 520x300
        // lisait comme une caisse posee sur le tapis.
        const standParts = [];
        const neck = tintedBox(150, neckH, 34, {
            top: 0x3a3f46,
            side: 0x23272c,
            front: 0x2d3238,
            bottom: 0x191b1e,
        });
        neck.translate(0, neckH / 2, 0);
        standParts.push(neck);
        // Cylindre a 36 pans mis a l'echelle : un ovale coute le meme prix
        // qu'un pave et se lit infiniment mieux de face.
        const foot = new THREE.CylinderGeometry(1, 1, 1, 36);
        foot.scale(300, 24, 180);
        foot.translate(0, 12, 0);
        paintVerticalGradient(foot, 0x1d2024, 0x474d55, 0.6);
        standParts.push(foot);
        const footTop = new THREE.CylinderGeometry(1, 1, 1, 36);
        footTop.scale(248, 7, 148);
        footTop.translate(0, 26, 0);
        paintVerticalGradient(footTop, 0x474d55, 0x5a626c, 1);
        standParts.push(footTop);
        standGroup.add(mergedMesh(standParts));
        this.scene.add(standGroup);
    }
    /**
     * (Re)builds the bezel body and its 4 bars for the current screen width,
     * so the frame always hugs the (resizable) panel.
     */
    fillBezel() {
        const w = SCREEN_SIZE.w;
        const h = SCREEN_SIZE.h;
        const t = BEZEL_T; // thin bezel = modern look
        const group = this.bezelGroup;
        for (const child of [...group.children]) {
            group.remove(child);
            child.geometry.dispose();
        }
        // Menton plus epais que les trois autres cotes, comme sur un vrai
        // moniteur : c'est lui qui porte l'electronique et la marque. Quatre
        // cotes identiques donnaient un simple passe-partout.
        const chin = t * 2.4;
        const parts = [];
        const shell = tintedBox(w + t * 1.5, h + t + chin - t * 0.5, 34, {
            top: 0x30353c,
            side: 0x22262b,
            front: 0x1b1e22,
            bottom: 0x191c20,
        });
        shell.translate(0, (t - chin) / 2, -30);
        parts.push(shell);
        const bar = (bw, bh, x, y) => {
            const b = tintedBox(bw, bh, 34, {
                top: 0x3c424a,
                side: 0x23272d,
                front: 0x2b3037,
                bottom: 0x1c1f23,
            });
            b.translate(x, y, 4);
            parts.push(b);
        };
        bar(w + t * 2, t, 0, h / 2 + t / 2); // haut
        bar(t, h + t * 2, -w / 2 - t / 2, 0); // gauche
        bar(t, h + t * 2, w / 2 + t / 2, 0); // droite
        bar(w + t * 2, chin, 0, -h / 2 - chin / 2); // menton
        // Pastille de marque sur le menton, dans le teal du logotype.
        const mark = tintedBox(Math.min(130, w * 0.06), chin * 0.2, 8, {
            top: 0x12c2b0,
            side: 0x0e9c8e,
            front: 0x12c2b0,
        });
        mark.translate(0, -h / 2 - chin / 2, 21);
        parts.push(mark);
        group.add(mergedMesh(parts));
    }
    /** Re-fit the bezel to the current screen width after a window resize. */
    resize() {
        if (this.bezelGroup)
            this.fillBezel();
    }
    /** Hide the old keyboard/mouse models and drop in procedural RGB gaming ones. */
    replacePeripherals() {
        const { kbGlow } = this.params();
        const keyboardNode = this.scene.getObjectByName('keyboard');
        if (keyboardNode) {
            const t = this.captureTransform(keyboardNode);
            // Le noeud baked est masque dans tous les cas : qu'il soit
            // remplace par le clavier procedural ou par celui du dossier 1,
            // il ne doit pas rester visible dessous.
            keyboardNode.visible = false;
            if (BRUNO_DESK) {
                const m = this.scene.getObjectByName('mouse');
                if (m)
                    m.visible = false;
                return;
            }
            const kb = this.buildGamingKeyboard(t.size, kbGlow);
            kb.position.copy(t.center);
            kb.position.y = t.box.min.y;
            kb.quaternion.copy(t.quaternion);
            this.scene.add(kb);
        }
        const mouseNode = this.scene.getObjectByName('mouse');
        if (mouseNode) {
            const t = this.captureTransform(mouseNode);
            mouseNode.visible = false;
            const mouse = this.buildGamingMouse(t.size);
            mouse.position.copy(t.center);
            mouse.position.y = t.box.min.y;
            mouse.quaternion.copy(t.quaternion);
            this.scene.add(mouse);
        }
    }
    /** World-space size/center/orientation of a node, read before hiding it. */
    captureTransform(node) {
        node.updateWorldMatrix(true, true);
        const box = new THREE.Box3().setFromObject(node);
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const quaternion = new THREE.Quaternion();
        node.getWorldQuaternion(quaternion);
        return { box, size, center, quaternion };
    }
    /**
     * Clavier TKL : chassis deux tons, plaque superieure en retrait et vraie
     * grille de touches aux largeurs ANSI. L'ancienne version etait un pave
     * noir uni surmonte de trois blocs RGB — aucune touche, donc rien qui
     * ressemblait a un clavier.
     *
     * La rangee de fonction est au fond (-Z), la barre d'espace devant (+Z),
     * cote camera : c'est le sens dans lequel un clavier est pose.
     */
    buildGamingKeyboard(size, glow) {
        const group = new THREE.Group();
        const W = size.x;
        const D = size.z;
        const H = Math.max(size.y, 70);
        const bodyH = H * 0.46;
        const plateH = H * 0.1;
        const capH = H * 0.26;
        const MARGIN_X = W * 0.022;
        const MARGIN_Z = D * 0.05;
        const MAIN_U = 15; // largeur ANSI du bloc principal
        const NAV_U = 3; // bloc de droite (navigation + fleches)
        const NAV_GAP_U = 0.35;
        // 0.5u de vide entre la rangee de fonction et les cinq autres.
        const ROWS_U = KB_ROWS.length + 0.5;
        // Une seule unite pour les deux axes, donc des touches carrees. On
        // prend la direction la plus contraignante : dimensionner sur la
        // largeur seule ferait deborder les touches hors du chassis en
        // profondeur, le modele d'origine etant plus large que profond.
        const u = Math.min((W - 2 * MARGIN_X) / (MAIN_U + NAV_GAP_U + NAV_U), (D - 2 * MARGIN_Z) / ROWS_U);
        const gap = u * 0.11;
        const blockW = (MAIN_U + NAV_GAP_U + NAV_U) * u;
        const blockD = ROWS_U * u;
        const x0 = -blockW / 2;
        const z0 = -blockD / 2;
        const capY = bodyH + plateH + capH / 2;
        // Centre en Z d'une rangee : la rangee de fonction occupe [0,1]u,
        // puis vient le vide de 0.5u, puis les rangees principales.
        const rowZ = (r) => z0 + (r === 0 ? 0.5 : r + 1) * u;
        const caps = [];
        const addCap = (cx, cz, wu) => {
            // Les touches larges sont les modificateurs : un ton plus sombre,
            // comme sur un vrai clavier, ce qui souligne la disposition.
            const mod = wu > 1.1;
            const cap = tintedBox(wu * u - gap, capH, u - gap, {
                top: mod ? 0x7b828d : 0x9aa1ac,
                side: mod ? 0x565c66 : 0x6e747e,
            });
            cap.translate(cx, capY, cz);
            caps.push(cap);
        };
        KB_ROWS.forEach((row, r) => {
            let cursor = 0;
            row.forEach((item) => {
                if (item < 0) {
                    cursor += -item;
                    return;
                }
                addCap(x0 + (cursor + item / 2) * u, rowZ(r), item);
                cursor += item;
            });
        });
        const navX0 = x0 + (MAIN_U + NAV_GAP_U) * u;
        KB_NAV.forEach((row, r) => {
            row.forEach((slot, c) => {
                if (!slot)
                    return;
                addCap(navX0 + (c + 0.5) * u, rowZ(r), 1);
            });
        });
        group.add(mergedMesh(caps));
        // --- Chassis ---
        const body = tintedBox(W, bodyH, D, {
            top: 0x4a505a,
            side: 0x343941,
            bottom: 0x23272d,
        });
        body.translate(0, bodyH / 2, 0);
        // Plaque superieure en retrait : c'est elle qu'on apercoit entre les
        // touches. Tres sombre, pour que les interstices lisent comme de
        // l'ombre et detachent chaque capuchon. Le liseret du chassis qui
        // depasse autour fait le bord de l'appareil.
        const inset = u * 0.22;
        const plate = tintedBox(W - 2 * inset, plateH, D - 2 * inset, {
            top: 0x23272d,
            side: 0x2d323a,
        });
        plate.translate(0, bodyH + plateH / 2, 0);
        group.add(mergedMesh([body, plate]));
        // --- Retroeclairage RGB sous le bord avant, en trois zones ---
        const litW = W * 0.86;
        for (let i = 0; i < 3; i++) {
            const mat = new THREE.MeshBasicMaterial({
                color: 0xff0000,
                transparent: true,
                opacity: 0.85,
            });
            this.rgbMaterials.push(mat);
            const strip = new THREE.Mesh(new THREE.BoxGeometry((litW / 3) * 0.94 * glow, bodyH * 0.16, 4), mat);
            // A cheval sur la face avant : il faut qu'il depasse pour se
            // voir, mais le garder au ras evite d'allonger l'empreinte du
            // clavier au-dela du modele d'origine. Le demi-millimetre qui
            // mord dans le chassis empeche le z-fighting avec sa face avant.
            strip.position.set(-litW / 2 + (litW / 3) * (i + 0.5), bodyH * 0.3, D / 2 + 1.5);
            group.add(strip);
        }
        return group;
    }
    /**
     * Souris : coque galbee, rainure entre les deux boutons, molette eclairee
     * et semelle. L'ancienne version etait une sphere aplatie ceinturee d'un
     * anneau — aucun des reperes qui font reconnaitre une souris.
     *
     * L'avant (boutons, molette) pointe vers -Z, loin de la camera : c'est le
     * sens dans lequel une souris est posee face a l'ecran.
     */
    buildGamingMouse(size) {
        const group = new THREE.Group();
        const W = Math.max(size.x, 150);
        const D = Math.max(size.z, 280);
        const H = Math.max(size.y, 90);
        const baseH = Math.max(4, H * 0.04);
        const shellH = H - baseH;
        // Profil longitudinal, tz de -1 (avant) a +1 (arriere, cote paume).
        // Le sommet est un peu en arriere du centre, comme la bosse d'une
        // vraie souris, et l'avant s'affaisse pour laisser place aux boutons.
        const heightAt = (tz) => {
            const k = tz - 0.18;
            return (1 -
                0.4 * Math.pow(Math.max(0, -k), 1.5) -
                0.3 * Math.pow(Math.max(0, k), 2.2));
        };
        // Profil en largeur : au plus large au milieu, resserre a l'avant
        // sous les doigts, arrondi a l'arriere.
        const widthAt = (tz) => 1 -
            0.3 * Math.pow(Math.max(0, -tz), 1.6) -
            0.18 * Math.pow(Math.max(0, tz), 2.2);
        // Hauteur de la coque sur l'axe median (x = 0). La section est un
        // demi-cercle, d'ou le sqrt(1 - tz²) avant mise a l'echelle. Sert a
        // poser rainure, molette et logo exactement sur la surface au lieu de
        // les faire flotter ou s'enfoncer.
        const crestY = (tz) => baseH +
            shellH * heightAt(tz) * Math.sqrt(Math.max(0, 1 - tz * tz));
        // --- Coque : demi-ellipsoide deforme ---
        const shellGeo = new THREE.SphereGeometry(1, 40, 22, 0, Math.PI * 2, 0, Math.PI / 2);
        const pos = shellGeo.attributes.position;
        const colors = new Float32Array(pos.count * 3);
        // Interpolation faite en lineaire, apres conversion : un degrade
        // calcule sur des valeurs sRGB s'eclaircit trop vite au milieu.
        const low = srgb(0x3e444d);
        const high = srgb(0xaeb5bf);
        const c = new THREE.Color();
        for (let i = 0; i < pos.count; i++) {
            // Les trois composantes sont lues avant d'ecrire : le facteur de
            // largeur depend de z, qu'on ecraserait sinon.
            const ux = pos.getX(i);
            const uy = pos.getY(i); // 1 au sommet, 0 au bord inferieur
            const uz = pos.getZ(i);
            pos.setXYZ(i, ux * (W / 2) * widthAt(uz), baseH + uy * shellH * heightAt(uz), uz * (D / 2));
            // Degrade vertical : faute de lumiere dans la scene, c'est lui
            // qui donne le galbe.
            c.copy(low).lerp(high, Math.pow(uy, 0.75));
            colors[i * 3] = c.r;
            colors[i * 3 + 1] = c.g;
            colors[i * 3 + 2] = c.b;
        }
        pos.needsUpdate = true;
        shellGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        group.add(new THREE.Mesh(shellGeo, new THREE.MeshBasicMaterial({ vertexColors: true })));
        // --- Semelle, pour que la souris se pose au lieu de flotter ---
        const base = tintedBox(W * 0.88, baseH, D * 0.94, {
            top: 0x23272d,
            side: 0x16181d,
            bottom: 0x16181d,
        });
        base.translate(0, baseH / 2, 0);
        group.add(mergedMesh([base]));
        // --- Rainure separant bouton gauche et bouton droit ---
        // Decoupee en segments qui suivent crestY : la coque est bombee, une
        // barre droite flotterait a l'avant et s'enfoncerait au milieu.
        const grooveFrom = -0.92;
        const grooveTo = 0.12;
        const steps = 16;
        const groove = [];
        for (let i = 0; i < steps; i++) {
            const a = grooveFrom + ((grooveTo - grooveFrom) * i) / steps;
            const b = grooveFrom + ((grooveTo - grooveFrom) * (i + 1)) / steps;
            const mid = (a + b) / 2;
            const seg = tintedBox(W * 0.045, 6, ((b - a) * D) / 2 + 1, { top: 0x1a1d22, side: 0x1a1d22 });
            seg.translate(0, crestY(mid) - 2, (mid * D) / 2);
            groove.push(seg);
        }
        group.add(mergedMesh(groove));
        // --- Molette, posee dans la rainure ---
        const wheelTz = -0.52;
        const wheelR = D * 0.042;
        const wheelMat = new THREE.MeshBasicMaterial({
            color: 0x00ffff,
            transparent: true,
            opacity: 0.95,
        });
        this.rgbMaterials.push(wheelMat);
        const wheel = new THREE.Mesh(new THREE.CylinderGeometry(wheelR, wheelR, W * 0.06, 20), wheelMat);
        // L'axe d'un CylinderGeometry est Y ; une molette tourne autour de X.
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(0, crestY(wheelTz) - wheelR * 0.45, (wheelTz * D) / 2);
        group.add(wheel);
        // --- Logo eclaire sur la bosse ---
        const logoTz = 0.34;
        const logoMat = new THREE.MeshBasicMaterial({
            color: 0x00ffff,
            transparent: true,
            opacity: 0.9,
        });
        this.rgbMaterials.push(logoMat);
        const logo = new THREE.Mesh(new THREE.CircleGeometry(W * 0.085, 24), logoMat);
        // Pose a plat sur le haut de la bosse, ou la surface est quasi
        // horizontale.
        logo.rotation.x = -Math.PI / 2;
        logo.position.set(0, crestY(logoTz) + 1.5, (logoTz * D) / 2);
        group.add(logo);
        return group;
    }
    update() {
        const t = this.time.elapsed * 0.0004;
        this.rgbMaterials.forEach((mat, i) => {
            const hue = (t + i * 0.25) % 1;
            mat.color.setHSL(hue, 1, 0.55);
        });
    }
}
